import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { cn } from '../cn.ts'

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        'w-full rounded-tile border border-line-2 bg-card px-4 py-2.5 text-sm text-text-1 placeholder:text-meta',
        'focus:border-pop focus:outline-none focus:ring-2 focus:ring-pop/25',
        className,
      )}
      {...rest}
    />
  )
}

/** Native select as a pill. `fit` sizes it to its content (filter chips) instead of the full row. */
export function Select({
  className,
  children,
  fit,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { fit?: boolean }) {
  return (
    <select
      className={cn(
        'h-10 appearance-none rounded-pill border border-line-2 bg-card px-4 text-sm text-text-1',
        fit ? 'w-auto' : 'w-full',
        'focus:border-pop focus:outline-none focus:ring-2 focus:ring-pop/25',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  )
}

/** Label + control + optional hint, stacked. */
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children
    <label className="flex flex-col gap-1.5">
      <span className="meta-label">{label}</span>
      {children}
      {hint && <span className="text-[12px] text-meta">{hint}</span>}
    </label>
  )
}

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-[6px] border border-line-2 bg-grey-1 px-1 font-mono text-[11px] text-meta',
        className,
      )}
    >
      {children}
    </kbd>
  )
}
